import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';
import noop from 'lodash/noop';
import cond from 'lodash/cond';
import { SuiContainer } from '@splashthat/sui';
import { Badge } from '@splashthat/doppler/Badge';

import { FormRenderer } from 'common/compounds/FormRenderer';
import { getEventSubdomainIfAny } from 'common/entities/event/selectors';
import { FORM_TYPE_RSVP } from 'common/entities/form';
import { getForm, getFormLibraryFormName, getFormLibraryFormId } from 'common/entities/form/selectors';
import { getFormSessionStepsEnabled } from 'common/entities/form/sessionRegistration';
import { getFormDisplayStyles } from 'common/entities/form/styleConfiguration/selectors';
import {
    DISPLAY_MODE_STANDARD,
    DISPLAY_MODE_PANEL,
    PANEL_DISPLAY_RIGHT,
    PANEL_DISPLAY_LEFT,
    DISPLAY_MODE_FULL_SCREEN,
    DISPLAY_MODE_WIDTH,
} from 'common/entities/form/styleConfiguration/constants';
import { containerPaddingM } from 'common/styles/variables';
import { getCanUserUpdateLibraryFormsUserPermission } from 'common/store/userPermissions/selectors';
import { notifyError } from 'common/compounds/Notification/modules';
import { getFormSessionStep } from 'common/compounds/FormRenderer/selectors';
import { FORM_SESSIONS_STEPS } from 'common/compounds/FormRenderer/constants/sessions';

import FormBuilderToolbar from './FormBuilderToolbar';
import { saveFormData } from '../../actions';

import { getFormId, getFormIsSaving } from '../../selectors';
import ScaledFormSaveModal from '../ScaledForms/ScaledFormSaveModal';
import { getIsFormSavedInLibrary, getScaledFormModalStatus } from '../../selectors/formLibrary';
import { saveFormAsCopy, setScaledFormModalStatusOpen } from '../../actions/formLibrary';
import { getHasAccessToLibraryComponents } from '../../../FormLibrary/selectors';
import { getFormLibraryManager, getFormLibraryRestrictedAccess } from '../../../../store/userProperties/selectors';

const PANEL_DISPLAY = {
    [PANEL_DISPLAY_RIGHT]: 'flex-end',
    [PANEL_DISPLAY_LEFT]: 'flex-start',
};

const FORM_DETACHED = 'DETACHED';
const FORM_SAVE_RETROACTIVELY = 'SAVE_RETROACTIVELY';
const FORM_DETACHABLE = 'DETACHABLE';
const NOT_ALLOWED = 'NOT_ALLOWED';

const NOT_ALLOWED_MESSSAGE = 'An internal error has occurred. Please refresh the page.';

const ModalContainer = styled.div`
    overflow: auto;
    height: ${(props) => (props.displayMode === DISPLAY_MODE_STANDARD ? 'auto' : '100%')};
    padding: ${(props) =>
        props.displayMode === DISPLAY_MODE_STANDARD
            ? `${containerPaddingM} ${containerPaddingM} 5rem ${containerPaddingM}`
            : '0'};
`;

const PanelDisplayContainer = styled.div`
    height: 100%;
    display: flex;
    justify-content: ${(props) => PANEL_DISPLAY[props.panelDisplay]};
`;

const DisplayModeContainer = styled.div`
    width: ${(props) => DISPLAY_MODE_WIDTH[props.displayMode]};
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const StepBadge = styled(Badge)`
    margin: 0.5rem;
`;

const RSVP_FORM_LABEL = 'RSVP Form';
export const DEFAULT_FORM_LABEL = 'Form';

const BASE_CLASS_NAME = 'FormBuilderPreviewPane';

const FormBuilderPreviewPane = (props) => {
    const saveForm = () => {
        const methods = {
            [FORM_DETACHED]: () => props.saveFormData(props.formId),
            [FORM_SAVE_RETROACTIVELY]: () => props.setScaledFormModalStatusOpen(true),
            [FORM_DETACHABLE]: () => props.saveFormAsCopy(props.formId),
            [NOT_ALLOWED]: () => props.notifyError(NOT_ALLOWED_MESSSAGE),
        };

        return methods[props.saveMethod]();
    };
    const isScaledFormSaveModalOpen = useSelector(getScaledFormModalStatus);

    const formId = useSelector(getFormId);
    const isSessionsStep = useSelector((state) => getFormSessionStepsEnabled(state, formId));
    const sessionStep = useSelector((state) => getFormSessionStep(state, formId));

    const shadowClassname = props.displayMode !== DISPLAY_MODE_STANDARD ? `${BASE_CLASS_NAME}__shadow` : '';

    return (
        <SuiContainer className={BASE_CLASS_NAME} variation="flex-vertical" grow>
            <FormBuilderToolbar
                toolbarTitle={props.toolbarTitle}
                closeButtonLabel="Exit Form Builder"
                closeForm={props.closeForm}
                saveForm={props.isFormLibraryWorkspace ? props.openSaveModal : saveForm}
                isSaving={props.isSaving}
                eventDomain={props.eventDomain}
                isFormLibraryWorkspace={props.isFormLibraryWorkspace}
                formName={props.formName}
            />
            <ModalContainer displayMode={props.displayMode}>
                <SuiContainer className={`${BASE_CLASS_NAME}__form`} variation="flex-center">
                    <SuiContainer className={shadowClassname}>
                        {isSessionsStep ? (
                            <StepBadge vibe="accent">
                                {sessionStep === FORM_SESSIONS_STEPS.GUEST_INFORMATION ? 'Step 1' : 'Step 2'}
                            </StepBadge>
                        ) : null}
                        <SuiContainer variation="shadow">
                            <PanelDisplayContainer panelDisplay={props.panelDisplay}>
                                <DisplayModeContainer displayMode={props.displayMode}>
                                    <FormRenderer onRequestClose={noop} formId={props.formId} />
                                </DisplayModeContainer>
                            </PanelDisplayContainer>
                        </SuiContainer>
                    </SuiContainer>
                </SuiContainer>
            </ModalContainer>
            <ScaledFormSaveModal
                open={isScaledFormSaveModalOpen}
                onClose={() => {
                    props.setScaledFormModalStatusOpen(false);
                }}
                formName={props.formName}
                formId={props.formId}
                formLibraryFormId={props.formLibraryFormId}
            />
        </SuiContainer>
    );
};

FormBuilderPreviewPane.propTypes = {
    toolbarTitle: PropTypes.string.isRequired,
    closeForm: PropTypes.func,
    saveFormData: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    formId: PropTypes.string.isRequired,
    formLibraryFormId: PropTypes.string,
    displayMode: PropTypes.oneOf([DISPLAY_MODE_STANDARD, DISPLAY_MODE_PANEL, DISPLAY_MODE_FULL_SCREEN]),
    panelDisplay: PropTypes.oneOf([PANEL_DISPLAY_RIGHT, PANEL_DISPLAY_LEFT]),
    eventDomain: PropTypes.string,
    formName: PropTypes.string,
    isFormLibraryWorkspace: PropTypes.bool,
    openSaveModal: PropTypes.func,
    saveFormAsCopy: PropTypes.func,
    setScaledFormModalStatusOpen: PropTypes.func,
    saveMethod: PropTypes.oneOf([FORM_DETACHED, FORM_SAVE_RETROACTIVELY, FORM_DETACHABLE, NOT_ALLOWED]).isRequired,
    notifyError: PropTypes.func,
};

FormBuilderPreviewPane.defaultProps = {
    closeForm: null,
    isSaving: false,
    formLibraryFormId: null,
    displayMode: DISPLAY_MODE_STANDARD,
    panelDisplay: PANEL_DISPLAY_LEFT,
    eventDomain: null,
    formName: undefined,
    isFormSavedInLibrary: false,
    isFormLibraryWorkspace: false,
    openSaveModal: null,
    saveFormAsCopy: () => {},
    setScaledFormModalStatusOpen: () => {},
    notifyError: () => {},
};

const formTypesToTopBarTitles = {
    [FORM_TYPE_RSVP]: RSVP_FORM_LABEL,
    default: DEFAULT_FORM_LABEL,
};

const mapStateToProps = (state, props) => {
    const formId = getFormId(state);
    const isSaving = getFormIsSaving(state, formId);
    const form = getForm(state, getFormId(state));
    const { displayMode, panelDisplay } = getFormDisplayStyles(state, formId);

    const eventDomain = getEventSubdomainIfAny(state);
    const formName = getFormLibraryFormName(state, formId);
    const formLibraryFormId = getFormLibraryFormId(state, formId);
    const isFormSavedInLibrary = getIsFormSavedInLibrary(state, formId);

    // If the form is RSVP and user is in the same org as event
    const hasAccessToLibraryComponents = getHasAccessToLibraryComponents(state, formId) && isFormSavedInLibrary;
    const hasFormLibraryManager =
        (hasAccessToLibraryComponents && getFormLibraryManager(state)) || Boolean(props.isFormLibraryWorkspace);
    const hasRestrictedAccess = getFormLibraryRestrictedAccess(state) && hasAccessToLibraryComponents;

    const userCanUpdateLibraryForms = getCanUserUpdateLibraryFormsUserPermission(state);
    const shouldUsePermissions = hasAccessToLibraryComponents && props.isUsingRoleBasedPermissions;

    const saveMethod = cond([
        [() => !isFormSavedInLibrary, () => FORM_DETACHED],
        [
            () => (shouldUsePermissions ? userCanUpdateLibraryForms : hasFormLibraryManager),
            () => FORM_SAVE_RETROACTIVELY,
        ],
        [() => (shouldUsePermissions ? !userCanUpdateLibraryForms : hasRestrictedAccess), () => FORM_DETACHABLE],
        [() => true, () => NOT_ALLOWED],
    ])();

    return {
        formId,
        formLibraryFormId,
        isSaving,
        displayMode,
        panelDisplay,
        toolbarTitle: formTypesToTopBarTitles[form?.type] || formTypesToTopBarTitles.default,
        eventDomain,
        formName,
        isFormLibraryWorkspace: props.isFormLibraryWorkspace,
        saveMethod,
    };
};

const mapDispatchToProps = {
    saveFormData,
    saveFormAsCopy,
    notifyError,
    setScaledFormModalStatusOpen,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormBuilderPreviewPane);
